import { VFC, useEffect, useState, useCallback } from 'react';
import DatePicker from 'react-datepicker';
import { HStack, Text } from '@chakra-ui/react';
import { useLoginUser } from '../../../hooks/providers/useLoginUserProvider';
import { diff_days } from '../../../hooks/useDateUtils';
import "react-datepicker/dist/react-datepicker.css";

type Props = {
  toDate: Date|null;
  fromDate: Date|null;
  setToDate: (date:Date|null)=>void;
  setFromDate: (date:Date|null)=>void;
  setDateErrFlag: (flag:boolean)=>void;
  defaultSearchDays: number;
  maxSearchDays: number;
};

export const PeriodInput: VFC<Props> = (props) => {
  const { toDate, fromDate, setFromDate, setToDate, setDateErrFlag, defaultSearchDays, maxSearchDays } = props;
  const { loginUser } = useLoginUser();
  const [ dateErrMes, setDateErrMes ] = useState('');

  // 初期実行(リージョンマスタ取得 $ グループ全検索)
  useEffect(() => {
    if( fromDate === null ){
      // カレンダーの初期設定
      const from_day = new Date();
      from_day.setDate(from_day.getDate() - defaultSearchDays + 1);
      setFromDate(from_day);
    }

    if( toDate === null ){
      const to_day = new Date();
      setToDate(to_day);
    }
  }, [defaultSearchDays, setFromDate, setToDate, fromDate, toDate]);

  // fromDateが設定された場合、期間を超える場合は、toDateを自動変更する
  const changeFromDate = useCallback((tmpFromDate: Date|null) => {
    if( tmpFromDate === null ) return;
    
    setFromDate(truncateHms(tmpFromDate));
    const duration = diff_days(tmpFromDate, toDate);
    // console.log(maxSearchDays,duration,tmpFromDate,toDate);
    if (duration >= maxSearchDays) {
      // toDateの計算
      const tmpToDate = new Date(tmpFromDate.getTime());
      tmpToDate.setDate(tmpToDate.getDate() + maxSearchDays - 1);
      // console.log(tmpToDate);
      setToDate(tmpToDate);
    }
    
  }, [toDate]);
  
  // toDateが設定された場合、期間を超える場合は、fromDateを自動変更する
  const changeToDate = useCallback((tmpToDate: Date|null) => {
    if( tmpToDate === null ) return;
    
    setToDate(truncateHms(tmpToDate));
    const duration = diff_days(fromDate,tmpToDate);
    // console.log(maxSearchDays,duration,fromDate,tmpToDate);
    if (duration >= maxSearchDays) {
      // fromDateの計算
      const tmpFromDate = new Date(tmpToDate.getTime());
      tmpFromDate.setDate(tmpFromDate.getDate() - maxSearchDays + 1);
      // console.log(tmpFromDate);
      setFromDate(tmpFromDate);
    }
    
  }, [fromDate]);
  
  // エラーチェック
  useEffect(() => {
    setDateErrMes('');
    setDateErrFlag(false);
    
    // console.log(fromDate,toDate);
    
    // 開始日時、 終了日時は必須
    if( fromDate === null || toDate === null ){
      setDateErrMes('※ 日付を入力してください ');
      setDateErrFlag(true);
      return;
    }

    // 開始日時<= 終了日時の確認
    if (fromDate && toDate) {
      if (!(fromDate <= toDate)) {
        setDateErrMes('※ 開始日時より終了日時を新しくしてください ');
        setDateErrFlag(true);
        return;
      }
    }

    // 日付差を計算する
    const duration = diff_days(fromDate, toDate);
    if (loginUser !== null) {
      if (duration > maxSearchDays) {
        const massge = '検索可能な日数を超えています。上限'.concat(
          String(maxSearchDays), '日'
        );
        setDateErrMes(massge);
        setDateErrFlag(true);
        return;
      }
    }
  }, [fromDate, toDate, loginUser, setDateErrMes, setDateErrFlag, maxSearchDays]);
  
  // 日付の時分秒を切り捨てる関数
  const truncateHms = (date: Date|null) => {
    /*
        引数がfalsy(null)の時、nullを返す。
        null以外の時、日本時間の時分秒を切り捨てた日時に変換
      */
    if( date === null ) return null;
    date.setHours(0);
    date.setMinutes(0);
    date.setSeconds(0);
    date.setMilliseconds(0);
    return date;
  };
  
  return (
    <>
      <HStack>
        <DatePicker
          id="input-start-date"
          data-testid="input-start-date"
          dateFormat="yyyy/MM/dd"
          className="DatePicker"
          selected={fromDate}
          onChange={(date: Date|null) => changeFromDate(truncateHms(date))}
        />
        <Text marginRight="30px">～</Text>
        <DatePicker
          id="input-end-date"
          dateFormat="yyyy/MM/dd"
          className="DatePicker"
          selected={toDate}
          onChange={(date: Date|null) => changeToDate(truncateHms(date))}
        />
      </HStack>
      <Text color="tomato">{dateErrMes}</Text>
    </>
  );
};
